import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  CustomContainer,
  Form,
  useDispatch,
  connect,
} from "../../../atoms/DependencyHelper";
import ContactDetailsForm from "../../../atoms/newLineForms/creditCheckForms/contactDetailsForm/ContactDetailsForm";
import ContactDetailsInfo from "../../../atoms/newLineForms/creditCheckDetails/contactDetailsInfo/ContactDetailsInfo";
import CTAButton from "../../../atoms/CTAButton/CTAButton";
import {
  numRegEx,
  imagePrefix,
  cellPhoneRegEx,
  emailRegEx,
  africaCountryCode,
} from "../../../../utils/helperServices";
import { SET_CREDIT_CHECK_CONTACT_DATA } from "../../../../Redux/actionTypes/index";
import errorMessages from "../../../../config/errorMessages";
import Image from "../../../atoms/image/image";
import formStyles from "../CheckoutCreditCheck.module.scss";
import * as Analytics from "../../../../services/analyticsUtag";
import { journeyType } from "../../../../context/checkout-journey-context";

const analyticsTags = [
  "page_name",
  "page_channel",
  "page_master_tab",
  "visitor_login_status",
  "visitor_customer_id",
  "visitor_id_hash",
  "visitor_msisdn_hash",
  "visitor_subscription_type",
];
/**
 * Contact Details component holds the logic of contact detail form validation logic,
 * handling all the API calls and analytics
 */
const ContactDetails = ({
  customerStatus,
  profileData,
  onFormSubmit,
  contactDetailsData,
  authDetailsData,
  currentJourneyType,
  doCreditCheckDisable,
}) => {
  const [analytics, setAnalytics] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [displayEditMode, setDisplayEditMode] = useState(
    contactDetailsData ? false : true
  );

  const cloneOfErrorMessages = JSON.parse(
    JSON.stringify(errorMessages.creditCheckout.errors.contact)
  );

  const dispatch = useDispatch();

  /**
   * Form input field blur event handler
   *
   * @usage call fieldValidationCheck function
   * @usage Set the state of form errorStatus
   * @usage Enable or disable form submit button
   * @call onBlur event
   */
  const handleBlur = async (event, val, form) => {
    event.persist();
    await fieldValidationCheck(event, val, form);

    errorStatus[form] = await checkFormError(form);
    setState({
      ...state,
      errorStatus,
    });

    if (fieldErrors.fail) {
      setSubmitDisabled(true);
      let name = event.target.name;
      let errMsg = fieldErrorsMessage[form][name];
      inlineAnalytics(errMsg);
    } else {
      checkErrorStatus();
    }
  };

  useEffect(() => {
    if (contactDetailsData) {
      fieldValues.contactDetails.cellNumber = contactDetailsData.cellNumber;
      fieldValues.contactDetails.emailAddress = contactDetailsData.emailAddress;
    } else if (authDetailsData) {
      fieldValues.contactDetails.cellNumber = authDetailsData?.msisdn
        ? authDetailsData.msisdn
        : "";
      fieldValues.contactDetails.emailAddress = profileData?.email
        ? profileData.email
        : "";
    }

    if (contactDetailsData || profileData) {
      errorStatus.contactDetails = checkFormError("contactDetails");

      if (errorStatus.contactDetails) {
        setSubmitDisabled(true);
      } else {
        setSubmitDisabled(false);
      }

      setState({
        ...state,
        fieldValues,
        errorStatus,
      });
    }
  }, [contactDetailsData, profileData]);

  const [state, setState] = useState({
    validationsOn: {
      contactDetails: true,
    },
    errorStatus: {
      contactDetails: true,
    },
    validations: {
      contactDetails: {
        cellNumber: true,
        emailAddress: true,
      },
    },
    fieldErrors: {
      contactDetails: {
        cellNumber: false,
        emailAddress: false,
      },
      fail: false,
    },
    fieldValues: {
      contactDetails: {
        cellNumber: "",
        emailAddress: "",
      },
    },
    fieldErrorsMessage: cloneOfErrorMessages,
  });

  const { errorStatus, fieldErrors, fieldValues, fieldErrorsMessage } = state;

  /**
   * Form input field change event handler
   *
   * @usage call fieldValidationCheck function
   * @usage Set the state of form errorStatus
   * @usage Enable or disable form submit button
   * @call onChange event
   */
  const handleChange = async (event, val, form) => {
    event.persist();

    if (!analytics) {
      Analytics.link({
        ...Analytics.getUtagData(analyticsTags),
        page_form_event: "start",
        page_form_name: `checkout: credit check: ${customerStatus}: contact details`,
      });
      setAnalytics(true);
    }
    await updateFieldValues(event, val, form);
  };

  /**
   * Check form error status
   *
   * @usage check if form error status is true or false
   * @usage Enable or disable form submit button
   */
  function checkErrorStatus() {
    let errValues = Object.values(state.errorStatus);
    for (const err of errValues) {
      if (err) {
        setSubmitDisabled(true);
        return;
      }
    }
    setSubmitDisabled(false);
  }

  /**
   * update input field values
   *
   * @usage Set the state of fieldValues
   */
  const updateFieldValues = async (event, val, form) => {
    const name = event.target.name;
    const value = val ? val : event.target.value;
    fieldValues[form][name] = value;

    setState({
      ...state,
      fieldValues,
    });
  };

  /**
   * Check input field validation
   *
   * @usage Set the state of form fieldErrors, fieldErrorsMessage
   */
  const fieldValidationCheck = async (event, val, form) => {
    const name = event.target.name;
    const value = val ? val : event.target.value;

    switch (name) {
      case "cellNumber":
        if (state.validationsOn[form] && state.validations[form].cellNumber) {
          if (value?.length > 0) {
            if (!numRegEx.test(value)) {
              fieldErrors[form].cellNumber = true;
              fieldErrorsMessage[form].cellNumber =
                errorMessages.creditCheckout.contactDetails.cellNumber.onlyNumber;
            } else if (!cellPhoneRegEx.test(value)) {
              fieldErrors[form].cellNumber = true;
              fieldErrorsMessage[form].cellNumber =
                errorMessages.creditCheckout.contactDetails.cellNumber.startWith;
            } else if (africaCountryCode(value) && value.length < 11) {
              fieldErrors[form].cellNumber = true;
              fieldErrorsMessage[form].cellNumber =
                errorMessages.creditCheckout.errors.contact.contactDetails.cellNumber;
            } else if (value?.length < 10) {
              fieldErrors[form].cellNumber = true;
              fieldErrorsMessage[form].cellNumber =
                errorMessages.creditCheckout.errors.contact.contactDetails.cellNumber;
            } else {
              fieldErrors[form].cellNumber = false;
            }
          } else {
            fieldErrors[form].cellNumber = true;
            fieldErrorsMessage[form].cellNumber =
              errorMessages.creditCheckout.errors.contact.contactDetails.cellNumber;
          }
          fieldErrors.fail = fieldErrors[form].cellNumber;
        } else {
          fieldErrors[form].cellNumber = false;
        }
        break;

      case "emailAddress":
        if (state.validationsOn[form] && state.validations[form].emailAddress) {
          if (value?.length > 0) {
            if (!emailRegEx.test(value)) {
              fieldErrors[form].emailAddress = true;
              fieldErrorsMessage[form].emailAddress =
                errorMessages.creditCheckout.contactDetails.emailAddress.onlyValid;
            } else {
              fieldErrors[form].emailAddress = false;
            }
          } else {
            fieldErrors[form].emailAddress = true;
            fieldErrorsMessage[form].emailAddress =
              errorMessages.creditCheckout.errors.contact.contactDetails.emailAddress;
          }
          fieldErrors.fail = fieldErrors[form].emailAddress;
        } else {
          fieldErrors.emailaddress = false;
        }
        break;
    }

    setState({
      ...state,
      fieldErrors,
      fieldErrorsMessage,
    });
  };

  /**
   * Set the analytics(link)
   *
   * @call when there is any form field error occured
   */
  const inlineAnalytics = (message) => {
    Analytics.link({
      ...Analytics.getUtagData(analyticsTags),
      page_form_event: "error",
      page_errors: message,
      page_form_name: `checkout: credit check: ${customerStatus}: contact details`,
      page_error_type: "form",
    });
  };

  /**
   * Check entire form error
   *
   * @usage check if any form field value is empty or not
   * @return true/false
   */
  function checkFormError(form) {
    var values = state.fieldValues[form];
    var errors = state.fieldErrors[form];
    var entries = Object.entries(state.validations[form]);

    if (state.validationsOn[form]) {
      for (const [key, value] of entries) {
        if ((values[key] == "" && value) || errors[key]) {
          return true;
        }
      }
      return false;
    } else {
      return false;
    }
  }

  /**
   * Form submit handler
   *
   * @usage call submitForm funtion when there is no error
   * @call onSubmit event
   */
  const handleSubmit = (event) => {
    event.preventDefault();
    event.persist();
    setAnalyticsOnNextButton();

    if (!state.errorStatus["contactDetails"]) {
      submitForm();
    }
  };

  /**
   * Set the on page submit
   *
   * @call on next button click
   */
  const setAnalyticsOnNextButton = () => {
    Analytics.link({
      ...Analytics.getUtagData(analyticsTags),
      link_type: "o",
      link_name: `checkout: credit check: ${customerStatus}: contact details: next`,
    });
  };

  /**
   * Set the on page edit
   *
   * @call on edit event
   */
  const setAnalyticsOnPageEdit = () => {
    Analytics.link({
      ...Analytics.getUtagData(analyticsTags),
      link_type: "o",
      page_tool_event: "use",
      page_tool_name: `checkout: credit check: ${customerStatus}: edit contact details`,
    });
  };

  /**
   *
   * @usage submit the form with request payload
   */
  const submitForm = async () => {
    dispatch({
      type: SET_CREDIT_CHECK_CONTACT_DATA,
      payload: { ...state.fieldValues["contactDetails"] },
    });
    onFormSubmit(true);
    setDisplayEditMode(false);
    doCreditCheckDisable("contactDetails", false);

    Analytics.link({
      ...Analytics.getUtagData(analyticsTags),
      page_form_event: "success",
      page_form_name: `checkout: credit check: ${customerStatus}: contact details`,
    });
  };

  /**
   *
   * @usage open form in edit mode and set analytics on click of edit button
   */
  const clickOnEditButton = () => {
    setDisplayEditMode(true);
    doCreditCheckDisable("contactDetails", true);
    setAnalyticsOnPageEdit();
  };

  return (
    <>
      <CustomContainer
        className={`${formStyles["container"]}`}
        id="contact-details"
      >
        <CustomContainer className={formStyles["form-heading"]}>
          {displayEditMode ? (
            <Image
              img_path={`${imagePrefix}/assets/images/icon-call.svg`}
              img_alt="section"
              className={formStyles["section-icon"]}
            />
          ) : (
            <Image
              img_path={`${imagePrefix}/assets/images/success-circle_small.svg`}
              img_alt="success"
              className={formStyles["section-success-icon"]}
            />
          )}
          Contact details
          {displayEditMode ? (
            <CustomContainer className={formStyles["section-numbers"]}>
              {currentJourneyType != journeyType.addLine
                ? "Section 2 of 7"
                : "Section 1 of 5"}
            </CustomContainer>
          ) : (
            <CustomContainer className={formStyles["section-numbers"]}>
              <a onClick={() => clickOnEditButton()}>
                <Image
                  img_path={`${imagePrefix}/assets/images/blog.svg`}
                  img_alt="edit"
                />
                <span>Edit details</span>
              </a>
            </CustomContainer>
          )}
        </CustomContainer>
        <hr></hr>
        {!displayEditMode ? (
          <ContactDetailsInfo data={contactDetailsData} />
        ) : (
          <Form
            noValidate
            id="checkout-credit-check-contact-form"
            onSubmit={handleSubmit}
          >
            <ContactDetailsForm
              formName={"contactDetails"}
              fieldValues={state.fieldValues["contactDetails"]}
              fieldErrors={state.fieldErrors["contactDetails"]}
              fieldErrorsMessage={state.fieldErrorsMessage["contactDetails"]}
              onChangeHandler={handleChange}
              onBlurHandler={handleBlur}
            />
            <CustomContainer className={formStyles["button-wrapper"]}>
              <CTAButton
                type="submit"
                variant="solid grey"
                disabled={submitDisabled}
                className={formStyles["button"]}
                id="checkout-credit-check-contact-form-submit"
              >
                Next
              </CTAButton>
            </CustomContainer>
          </Form>
        )}
      </CustomContainer>
    </>
  );
};

ContactDetails.propTypes = {
  customerStatus: PropTypes.string,
  onFormSubmit: PropTypes.func,
};

const mapStateToProps = (state) => {
  return {
    contactDetailsData: state.creditCheckDetails.contactDetails,
    authDetailsData: state.checkoutDetails.authDetails,
  };
};

export default connect(mapStateToProps)(ContactDetails);
